import { buildFacetOptions, type FacetDimension, type FacetFilterResult, type FacetOptionOrder, type FacetSelection } from "../../../utils/facetFilters";
import type { FilterSection } from "./filterFields";

/** One checkbox group of a facet rail or popover. */
export type FacetSpec<T> = {
	dimension: FacetDimension<T>;
	title: string;
	getLabel: (key: string) => string;
	/** Default true. */
	defaultExpanded?: boolean;
	searchable?: boolean;
	colorDot?: string;
	/** Default "label". */
	order?: FacetOptionOrder;
	hidden?: boolean;
};

const FACET_VISIBLE_LIMIT = 6;

type BuildFacetSectionsArgs<T> = {
	/** Unfiltered rows. */
	rows: readonly T[];
	facets: readonly FacetSpec<T>[];
	result: FacetFilterResult<T>;
	selection: FacetSelection;
	onToggle: (dimensionId: string, optionId: string, checked: boolean) => void;
	highlightedId: string | null;
	dataTestId: string;
};

/** Facet specs + `useFacetFilters` state → `FilterFieldList` sections (rail or popover). */
export function buildFacetSections<T>({ rows, facets, result, selection, onToggle, highlightedId, dataTestId }: BuildFacetSectionsArgs<T>): FilterSection[] {
	return facets.map((spec) => {
		const id = spec.dimension.id;
		// DEFAULT: options read alphabetically unless the spec asks for key order or a fixed list.
		const options = buildFacetOptions(rows, spec.dimension, result, selection, spec.getLabel, spec.order ?? "label").map((option) =>
			spec.colorDot ? { ...option, colorDot: spec.colorDot } : option,
		);
		return {
			id,
			hidden: spec.hidden,
			fields: [
				{
					kind: "facet",
					id,
					title: spec.title,
					options,
					onToggle: (option: string, checked: boolean) => onToggle(id, option, checked),
					defaultExpanded: spec.defaultExpanded,
					visibleLimit: FACET_VISIBLE_LIMIT,
					searchable: spec.searchable,
					highlighted: highlightedId === id,
					dataTestId: `${dataTestId}-${id}`,
				},
			],
		};
	});
}
